import type { AlertBanner } from '@generated/graphql';
import {
  Box,
  Button,
  IconButton,
  Stack,
  type SxProps,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { Close as CloseIcon } from 'hephaestus/webIcons';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { BANNER_TYPE_OPTIONS } from './alertBanner.util';

export type AlertBannerItemProp = {
  bannerFields: Pick<
    Partial<AlertBanner>,
    'bannerType' | 'bannerCopy' | 'configuration' | 'actionButton'
  >;
  onDismiss?: () => void;
  onAction?: () => void;
  sx?: SxProps;
};

export const AlertBannerItem = ({
  bannerFields,
  onDismiss,
  onAction,
  sx,
}: AlertBannerItemProp) => {
  const { t } = useTranslation();
  const { translateBackendString } = useLocalization();
  const theme = useTheme();

  const isDismissible = bannerFields.configuration?.makeDismissible ?? false;
  const hasAction = bannerFields.configuration?.enableAction ?? false;
  const actionLabel = bannerFields.actionButton?.label || '';

  const config = BANNER_TYPE_OPTIONS.find(
    (option) => option.key === bannerFields.bannerType,
  );

  if (!config) return null;

  const supportColor = theme.palette[config.supportColor];
  const IconComponent = config.icon;
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minHeight: '48px',
          bgcolor: theme.palette[config.backgroundColor],
          py: 0,
          px: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack
        useFlexGap
        spacing={1}
        direction='row'
        sx={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}
      >
        <IconComponent fontSize='inherit' />
        <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
          {translateBackendString(bannerFields.bannerCopy)}
        </Typography>
      </Stack>

      {(hasAction || isDismissible) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {hasAction && actionLabel && (
            <Button
              variant='outlined'
              size='small'
              onClick={onAction}
              sx={{
                borderColor: `${supportColor}`,
                '&:hover': {
                  borderColor: `${supportColor}`,
                  backgroundColor: `${supportColor}14`,
                },
              }}
            >
              {actionLabel}
            </Button>
          )}

          {isDismissible && (
            <IconButton
              aria-label={t('shared:close')}
              size='xsmall'
              onClick={onDismiss}
              sx={{
                color: supportColor,
                '&:hover': {
                  borderColor: `${supportColor}`,
                  backgroundColor: `${supportColor}14`,
                },
              }}
            >
              <CloseIcon fontSize='inherit' />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};
